import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi2'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import { CommercialOnly } from '@/components/common/CommercialOnly'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { SubscriberPicker } from '@/components/common/SubscriberPicker'
import Label from '@/components/form/Label'
import TextInput from '@/components/form/TextInput'
import { toggleVisibleMemberIds } from '@/components/organizations/AddOrganizationModal/selection'
import { CuiTooltip } from '@/components/organizations/Cui'

import { IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useEntitlementCheck } from '@/hooks/entitlements'
import { useHandleOrgChange } from '@/hooks/navigation/useHangleOrgChange'
import { useCreateOrganization } from '@/hooks/organization/useCreateOrganization'
import { useUpdateDefaultOrganization } from '@/hooks/organization/useUpdateDefaultOrganization'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { CreateOrganization } from '@/types/Organization'
import type { User } from '@/types/User'

const EMPTY_USERS: User[] = []

interface AddOrganizationModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  initialCuiComplianceEnabled?: boolean
}

interface FormValues {
  orgName: string
  userIds: UUID[]
  cui_compliance_enabled: boolean
}

const AddOrganizationModal: React.FC<AddOrganizationModalProps> = ({
  open,
  setOpen,
  initialCuiComplianceEnabled = false,
}) => {
  const defaultCuiComplianceEnabled =
    IS_FEDRAMP_ENVIRONMENT || initialCuiComplianceEnabled
  const { user } = useAuthInfoWithThrow()
  const { data: billingOrganization, isLoading: isBillingOrganizationLoading } =
    useBillingOrganization()
  const { data: hasCuiEntitlement } = useEntitlementCheck({
    entitlement: 'cui_compliance',
    enabled: !IS_FEDRAMP_ENVIRONMENT,
  })
  const { mutateAsync: createOrganization } = useCreateOrganization()
  const { mutateAsync: updateDefaultOrganization } =
    useUpdateDefaultOrganization()

  const { handleOrgChange } = useHandleOrgChange()

  const currentUserId = user.userId as UUID
  const defaultUserIds = useMemo(
    () => (billingOrganization?.users.map(({ id }) => id) ?? []) as UUID[],
    [billingOrganization?.users],
  )

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      orgName: '',
      userIds: defaultUserIds,
      cui_compliance_enabled: defaultCuiComplianceEnabled,
    },
  })

  const users = useMemo(
    () =>
      billingOrganization?.users.toSorted((a: User, b: User) =>
        a.first_name.localeCompare(b.first_name),
      ) ?? EMPTY_USERS,
    [billingOrganization?.users],
  )

  const selectedUserIds = watch('userIds')
  const lockedUserIds = useMemo(() => [currentUserId], [currentUserId])
  const initializedForCurrentOpen = useRef(false)

  const resetForm = useCallback(() => {
    reset({
      orgName: '',
      userIds: defaultUserIds,
      cui_compliance_enabled: defaultCuiComplianceEnabled,
    })
  }, [defaultCuiComplianceEnabled, defaultUserIds, reset])

  useEffect(() => {
    if (!open) {
      initializedForCurrentOpen.current = false
      return
    }
    if (initializedForCurrentOpen.current || isBillingOrganizationLoading) {
      return
    }

    resetForm()
    initializedForCurrentOpen.current = true
  }, [isBillingOrganizationLoading, open, resetForm])

  const cuiComplianceEnabled = watch('cui_compliance_enabled')

  const handleClose = () => {
    resetForm()
    initializedForCurrentOpen.current = false
    setOpen(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm()
      initializedForCurrentOpen.current = false
    }
    setOpen(nextOpen)
  }

  const handleCuiComplianceEnabledChange = useCallback(
    (enabled: boolean) => () => {
      setValue('cui_compliance_enabled', enabled)
    },
    [setValue],
  )

  const handleToggleUser = useCallback(
    (userId: UUID) => {
      if (userId === currentUserId) {
        return
      }
      setValue(
        'userIds',
        selectedUserIds.includes(userId)
          ? selectedUserIds.filter((id) => id !== userId)
          : [...selectedUserIds, userId],
        { shouldDirty: true },
      )
    },
    [currentUserId, selectedUserIds, setValue],
  )

  const handleToggleAllUsers = useCallback(
    (visibleIds: UUID[]) => {
      setValue('userIds', toggleVisibleMemberIds(selectedUserIds, visibleIds), {
        shouldDirty: true,
      })
    },
    [selectedUserIds, setValue],
  )

  const onSubmit = async (data: FormValues) => {
    if (billingOrganization) {
      const org: CreateOrganization = {
        name: data.orgName,
        user_ids: Array.from(new Set([...data.userIds, currentUserId])),
        cui_compliance_enabled:
          IS_FEDRAMP_ENVIRONMENT || data.cui_compliance_enabled,
      }
      try {
        const newOrg = await createOrganization([billingOrganization.id, org])
        toast.success('Successfully created organization!')
        handleClose()
        handleOrgChange(newOrg.id)
        await updateDefaultOrganization([newOrg])
      } catch (err) {
        console.error('Failed to create organization:', err)
      }
    }
  }

  // see globals.css to see important info about pointer-events-none
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="relative flex max-h-[calc(100dvh-2rem)] flex-col gap-3 overflow-hidden p-5 sm:min-w-[640px] sm:max-w-[640px] md:max-h-[calc(100dvh-6rem)]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Name your organization and choose which teammates to bring with
              you.
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden pt-4">
            <div className="shrink-0 space-y-2">
              <label
                htmlFor="organizationName"
                className="block text-sm font-medium text-primary-900 dark:text-primary-100"
              >
                Name
              </label>
              <Controller
                name="orgName"
                control={control}
                rules={{ required: 'Organization name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    {...field}
                    type="text"
                    id="organizationName"
                    placeholder="Set an organization name"
                    autoComplete="off"
                    error={error?.message}
                  />
                )}
              />
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="flex shrink-0 items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    Members
                  </div>
                  <p className="text-pretty text-xs text-primary-500 dark:text-primary-400">
                    Everyone is selected by default. Search or deselect
                    teammates you don&apos;t want to include.
                  </p>
                </div>
                <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-primary-500 dark:text-primary-400">
                  {selectedUserIds.length} selected
                </span>
              </div>
              <SubscriberPicker
                users={users}
                orgId={billingOrganization?.id as UUID}
                isLoading={isBillingOrganizationLoading}
                selectedIds={selectedUserIds}
                currentUserId={currentUserId}
                onToggle={handleToggleUser}
                onToggleAll={handleToggleAllUsers}
                fillHeight={true}
                searchName="new-organization-member-search"
                emptyMessage="No teammates are available to add yet."
                noMatchesMessage="No teammates match your search."
                lockedIds={lockedUserIds}
                lockedHint="Always included"
              />
            </div>
          </div>
          <CommercialOnly>
            {billingOrganization && hasCuiEntitlement && (
              <div className="mt-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="cui_compliance_enabled">
                      Will this organization hold CUI data?
                    </Label>
                    <CuiTooltip>
                      <HiOutlineQuestionMarkCircle
                        size={16}
                        className="text-primary-500 dark:text-primary-400"
                      />
                    </CuiTooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant={
                        cuiComplianceEnabled
                          ? 'positive-compact'
                          : 'outline-compact'
                      }
                      onClick={handleCuiComplianceEnabledChange(true)}
                      className="w-28"
                    >
                      <span>Yes</span>
                    </Button>
                    <Button
                      type="button"
                      variant={
                        cuiComplianceEnabled
                          ? 'outline-compact'
                          : 'danger-compact'
                      }
                      onClick={handleCuiComplianceEnabledChange(false)}
                      className="w-28"
                    >
                      <span>No</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CommercialOnly>
          <DialogFooter className="shrink-0 pt-6">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline-compact"
              disabled={isSubmitting}
            >
              <span>Cancel</span>
            </Button>
            <Button
              type="submit"
              variant="primary-compact"
              disabled={!!errors.orgName || isSubmitting}
              isLoading={isSubmitting}
            >
              <span>Create Organization</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddOrganizationModal
